import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { PlusCircle, Pencil, Trash2, Search } from 'lucide-react';
import { SUBJECT_OPTIONS } from '../lib/subjectFields';
import { useAuth } from '../contexts/AuthContext';

const EMPTY_PARTNER = {
  name: '', categories: [], email_primary: '', email_secondary: '',
  contact_name: '', phone: '', geographic_zone: '', comments: '',
  active: true, default_relance_delay: 7, form_url: ''
};

const ALL_CATEGORIES = [
  ...SUBJECT_OPTIONS.map(s => s.value),
  'STOCKAGE', 'EFFACEMENT', 'CVC'
];

export default function PartnersPage() {
  const { user } = useAuth();
  const isInternal = user?.role === 'equipe_interne';
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_PARTNER });

  const fetchPartners = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (catFilter) params.category = catFilter;
      const res = await API.get('/partners', { params });
      setPartners(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchPartners(); }, [search, catFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_PARTNER });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editing) {
        await API.put(`/partners/${editing}`, form);
        toast.success('Partenaire mis a jour');
      } else {
        await API.post('/partners', form);
        toast.success('Partenaire cree');
      }
      setDialogOpen(false);
      fetchPartners();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    try {
      await API.delete(`/partners/${id}`);
      toast.success('Partenaire supprime');
      fetchPartners();
    } catch { toast.error('Erreur'); }
  };

  const toggleCategory = (cat) => {
    setForm(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat]
    }));
  };

  return (
    <div className="space-y-4 fade-in" data-testid="partners-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#0B1120]">Partenaires</h1>
          <p className="text-sm text-muted-foreground">{partners.length} partenaire{partners.length > 1 ? 's' : ''}</p>
        </div>
        {isInternal && (
          <Button className="bg-[#397DFF] hover:bg-[#2563EB] text-white gap-1" size="sm"
            onClick={openCreate} data-testid="add-partner-btn">
            <PlusCircle size={14} /> Ajouter
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border p-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Rechercher..." className="pl-9 h-9 text-sm" value={search}
              onChange={e => setSearch(e.target.value)} data-testid="partners-search" />
          </div>
          <Select value={catFilter} onValueChange={v => setCatFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="partners-cat-filter">
              <SelectValue placeholder="Toutes categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes categories</SelectItem>
              {ALL_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-semibold">Nom</TableHead>
              <TableHead className="text-xs font-semibold">Categories</TableHead>
              <TableHead className="text-xs font-semibold">Email</TableHead>
              <TableHead className="text-xs font-semibold">Zone</TableHead>
              <TableHead className="text-xs font-semibold">Actif</TableHead>
              {isInternal && <TableHead className="text-xs font-semibold w-20">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Chargement...</TableCell></TableRow>
            ) : partners.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Aucun partenaire</TableCell></TableRow>
            ) : partners.map(p => (
              <TableRow key={p.id} data-testid={`partner-row-${p.id}`}>
                <TableCell className="font-medium text-sm">{p.name}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {p.categories.map(c => <Badge key={c} variant="outline" className="text-[9px]">{c}</Badge>)}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.email_primary}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.geographic_zone}</TableCell>
                <TableCell>
                  <div className={`w-2 h-2 ${p.active ? 'bg-success' : 'bg-slate-300'}`} style={{borderRadius:'50%'}} />
                </TableCell>
                {isInternal && (
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(p)}
                        data-testid={`edit-partner-${p.id}`}>
                        <Pencil size={13} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => handleDelete(p.id)}
                        data-testid={`delete-partner-${p.id}`}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Modifier' : 'Nouveau'} partenaire</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Nom *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="h-9 text-sm" data-testid="partner-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Email principal</Label>
                <Input value={form.email_primary} onChange={e => setForm(p => ({ ...p, email_primary: e.target.value }))}
                  className="h-9 text-sm" data-testid="partner-form-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Email secondaire</Label>
                <Input value={form.email_secondary} onChange={e => setForm(p => ({ ...p, email_secondary: e.target.value }))}
                  className="h-9 text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Contact</Label>
                <Input value={form.contact_name} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))}
                  className="h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Telephone</Label>
                <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  className="h-9 text-sm" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Zone geographique</Label>
              <Input value={form.geographic_zone} onChange={e => setForm(p => ({ ...p, geographic_zone: e.target.value }))}
                className="h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">URL formulaire partenaire (HubSpot, etc.)</Label>
              <Input value={form.form_url} onChange={e => setForm(p => ({ ...p, form_url: e.target.value }))}
                placeholder="https://..."
                className="h-9 text-sm" data-testid="partner-form-url" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Categories</Label>
              <div className="flex flex-wrap gap-2">
                {ALL_CATEGORIES.map(cat => (
                  <label key={cat} className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox checked={form.categories.includes(cat)} onCheckedChange={() => toggleCategory(cat)} />
                    <span className="text-xs">{cat}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={v => setForm(p => ({ ...p, active: v }))}
                data-testid="partner-form-active" />
              <Label className="text-xs">Actif</Label>
            </div>
            <Button className="w-full bg-[#397DFF] hover:bg-[#2563EB] text-white" onClick={handleSubmit}
              data-testid="partner-form-submit">
              {editing ? 'Mettre a jour' : 'Creer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
